import * as React from 'react';
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { SyncIcon } from '@patternfly/react-icons';
import toast from 'react-hot-toast';
import { AdminUserSummary, FILTERABLE_ROLES } from '@app/Users/types';
import { getUsers } from '@app/Users/users.api';
import { RoleChips } from '@app/Users/RoleChips';
import { Pill } from '@app/Campaigns/badges';
import { colors, radii, shadows } from '@app/components/designTokens';

const th: React.CSSProperties = {
  textAlign: 'left',
  padding: '12px 16px',
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.4px',
  color: colors.subtle,
  borderBottom: `1px solid ${colors.border}`,
  whiteSpace: 'nowrap'
};
const td: React.CSSProperties = {
  padding: '13px 16px',
  fontSize: 14,
  color: colors.body,
  borderBottom: `1px solid ${colors.border}`,
  verticalAlign: 'middle'
};

const subTone = (status: string | null): 'green' | 'amber' | 'red' | 'gray' => {
  switch (status) {
    case 'ACTIVE':
      return 'green';
    case 'TRIALING':
      return 'amber';
    case 'PAST_DUE':
    case 'INCOMPLETE':
      return 'red';
    default:
      return 'gray';
  }
};

export const Users: React.FunctionComponent = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<AdminUserSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [hovered, setHovered] = useState<number | null>(null);

  const load = useCallback(
    async (spinner: boolean) => {
      if (spinner) setLoading(true);
      try {
        const data = await getUsers({ q: search || undefined, role: roleFilter || undefined, limit: 200 });
        setUsers(data);
        setError(null);
      } catch (e: any) {
        setError(e?.message || 'Failed to load users');
        if (!spinner) toast.error('Could not refresh users');
      } finally {
        if (spinner) setLoading(false);
      }
    },
    [search, roleFilter]
  );

  useEffect(() => {
    load(true);
  }, [load]);

  const styles: { [k: string]: React.CSSProperties } = {
    wrap: { padding: 24 },
    headerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 16 },
    title: { fontSize: 22, fontWeight: 700, color: colors.title, margin: 0 },
    subtle: { fontSize: 13, color: colors.subtle },
    refreshBtn: { display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 14px', border: `1px solid ${colors.border}`, borderRadius: radii.button, background: '#fff', color: colors.body, fontSize: 13, fontWeight: 600, cursor: 'pointer' },
    toolbar: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 12 },
    search: { flex: 1, minWidth: 220, maxWidth: 340, padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: radii.input, fontSize: 14, outline: 'none' },
    select: { padding: '10px 12px', border: `1px solid ${colors.border}`, borderRadius: radii.input, fontSize: 14, background: '#fff', color: colors.body },
    card: { background: '#fff', borderRadius: radii.card, boxShadow: shadows.softCard, border: `1px solid ${colors.border}`, overflow: 'hidden' },
    metric: { fontWeight: 600 },
    metricSub: { fontSize: 12, color: colors.subtle }
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>Users</h1>
          <div style={styles.subtle}>{loading ? 'Loading…' : `${users.length} users loaded`}</div>
        </div>
        <button style={styles.refreshBtn} onClick={() => load(true)}>
          <SyncIcon className={loading ? 'logo-bolt-pulse' : undefined} /> Refresh
        </button>
      </div>

      <div style={styles.toolbar}>
        <input style={styles.search} placeholder="Search by email or name…" value={search} onChange={e => setSearch(e.target.value)} />
        <select style={styles.select} value={roleFilter} onChange={e => setRoleFilter(e.target.value)}>
          <option value="">All roles</option>
          {FILTERABLE_ROLES.map(r => (
            <option key={r} value={r}>
              {r.replace(/^ROLE_/, '')}
            </option>
          ))}
        </select>
      </div>

      {error && (
        <div style={{ background: colors.dangerRing, color: colors.danger, borderRadius: 10, padding: '12px 16px', fontSize: 14, marginBottom: 12 }}>{error}</div>
      )}

      <div style={styles.card}>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: colors.subtle }}>Loading users…</div>
        ) : users.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: colors.subtle }}>No users match these filters.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
              <thead>
                <tr>
                  <th style={th}>User</th>
                  <th style={th}>Roles</th>
                  <th style={th}>Ads</th>
                  <th style={th}>Campaigns</th>
                  <th style={th}>Runs</th>
                  <th style={th}>Subscription</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr
                    key={u.userId}
                    onClick={() => navigate(`/users/${u.userId}`)}
                    onMouseEnter={() => setHovered(u.userId)}
                    onMouseLeave={() => setHovered(null)}
                    style={{ cursor: 'pointer', background: hovered === u.userId ? '#f8fafc' : 'transparent' }}
                  >
                    <td style={td}>
                      <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                        {u.displayName || `User #${u.userId}`}
                        {!u.emailVerified && <Pill text="UNVERIFIED" tone="amber" />}
                      </div>
                      <div style={{ fontSize: 12, color: colors.subtle }}>{u.email}</div>
                    </td>
                    <td style={td}>
                      <RoleChips roles={u.roles} />
                    </td>
                    <td style={td}>
                      <span style={styles.metric}>{u.adsCreated}</span>
                      <div style={styles.metricSub}>{u.aiAdsCreated} AI</div>
                    </td>
                    <td style={td}>
                      <span style={styles.metric}>{u.campaignsCreated}</span>
                    </td>
                    <td style={td}>
                      <span style={styles.metric}>{u.runsBooked}</span>
                    </td>
                    <td style={td}>
                      {u.subscriptionTier ? (
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <strong>{u.subscriptionTier}</strong>
                          {u.subscriptionStatus && <Pill text={u.subscriptionStatus} tone={subTone(u.subscriptionStatus)} />}
                        </span>
                      ) : (
                        <span style={{ color: colors.placeholder }}>None</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
